<template>
  <div v-if="linhas.length" ref="raiz" class="ssp" @pointerenter="entrou" @pointerleave="saiu" @focusin="ganhouFoco" @focusout="perdeuFoco">
    <!-- Card fechado: um semáforo só (erro > rodando > aviso > tudo em dia > pausado) -->
    <button type="button" class="ssp-card" :class="'ssp-' + resumo.nivel" :aria-expanded="aberto ? 'true' : 'false'"
      aria-haspopup="true" :title="resumo.texto" @click="alternar" @keydown.esc="fechar">
      <span v-if="resumo.nivel === 'rodando'" class="ssp-spin" aria-hidden="true"></span>
      <span v-else class="ssp-dot" aria-hidden="true"></span>
      <span class="ssp-texto">{{ resumo.texto }}</span>
      <svg class="ssp-seta" :class="{ 'ssp-seta-aberta': aberto }" xmlns="http://www.w3.org/2000/svg" width="12" height="12"
        viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
        stroke-linejoin="round" aria-hidden="true">
        <polyline points="6 9 12 15 18 9"></polyline>
      </svg>
    </button>

    <!-- Detalhe em tempo real de cada ação -->
    <div v-show="aberto" class="ssp-pop" role="region" aria-label="Andamento das sincronizações">
      <div class="ssp-pop-titulo d-flex justify-content-between align-items-center">Sincronizações<AjudaBotao :slug="AJUDA_SLUGS.SISTEMA_STATUS_SINCRONIA" variante="popup" titulo="Ajuda: status das sincronizações" /></div>
      <div v-for="l in linhas" :key="l.id" class="ssp-linha" :class="'ssp-' + l.nivel">
        <span v-if="l.nivel === 'rodando'" class="ssp-spin" aria-hidden="true"></span>
        <span v-else class="ssp-dot" aria-hidden="true"></span>
        <div class="ssp-linha-corpo">
          <div class="ssp-linha-topo">
            <span class="ssp-linha-nome">{{ l.label }}</span>
            <span class="ssp-linha-dir">{{ l.direita }}</span>
          </div>
          <div v-if="l.detalhe" class="ssp-linha-detalhe">{{ l.detalhe }}</div>
          <div v-if="l.ultima" class="ssp-linha-ultima">{{ l.ultima }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import AjudaBotao from '@/components/AjudaBotao.vue';
import { AJUDA_SLUGS } from '@/config/ajudaEngage';
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useSyncStatus } from '@/composables/useSyncStatus.js'

// avisos: ações que o header mostra só como mensagem (portal/alerta desativado etc.): [{ id, label, texto }]
const props = defineProps({
  avisos: { type: Array, default: () => [] },
})

const { lista } = useSyncStatus()

const raiz = ref(null)
const hover = ref(false)
const fixado = ref(false)
const foco = ref(false) // foco por teclado (clique de mouse não conta, senão não dá para fechar clicando)
const aberto = computed(() => hover.value || fixado.value || foco.value)
let timerFechar = null

// Hover só vale para mouse; em toque o clique alterna (mouseenter emulado atrapalharia).
function entrou(e) {
  if (e.pointerType !== 'mouse') return
  clearTimeout(timerFechar)
  hover.value = true
}
function saiu(e) {
  if (e.pointerType !== 'mouse') return
  // pequeno atraso: o mouse atravessa o espaço entre o card e a lista sem fechar
  timerFechar = setTimeout(() => { hover.value = false }, 200)
}
function alternar() { fixado.value = !fixado.value }
function fechar() { hover.value = false; fixado.value = false; foco.value = false }
function ganhouFoco(e) { if (e.target.matches?.(':focus-visible')) foco.value = true }
function perdeuFoco(e) { if (!raiz.value?.contains(e.relatedTarget)) foco.value = false }
function foraDoComponente(e) {
  if (raiz.value && !raiz.value.contains(e.target)) fechar()
}

onMounted(() => document.addEventListener('pointerdown', foraDoComponente))
onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', foraDoComponente)
  clearTimeout(timerFechar)
})

const ORDEM_NIVEL = { erro: 0, rodando: 1, aviso: 2, ocioso: 3, fora: 4 }

const hhmm = (ms) => new Date(ms).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })

function relogio(seg) {
  const h = Math.floor(seg / 3600)
  const m = Math.floor((seg % 3600) / 60)
  const s = seg % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

// Une os motores (tempo real) com os avisos do header e ordena por gravidade.
const linhas = computed(() => {
  const dosMotores = lista.value.map((i) => {
    let direita = ''
    let detalhe = ''
    let ultima = ''
    if (i.estado === 'erro') {
      direita = i.erroEm ? `Falhou às ${hhmm(i.erroEm)}` : 'Falhou'
      detalhe = i.detalhe
    } else if (i.estado === 'rodando') {
      direita = 'Em andamento'
      detalhe = i.detalhe
    } else {
      if (i.segundos != null) direita = `${i.rotuloSegundos} ${relogio(i.segundos)}`
      else direita = i.estado === 'fora' ? 'Em pausa' : 'Concluído'
      detalhe = i.detalhe
    }
    if (i.estado !== 'erro' && i.ultimaEm) ultima = `Última: ${hhmm(i.ultimaEm)}${i.ultimoResultado ? ' · ' + i.ultimoResultado : ''}`
    return { id: i.id, label: i.label, nivel: i.estado, ordem: i.ordem ?? 99, segundos: i.segundos, direita, detalhe, ultima }
  })
  const dosAvisos = props.avisos.map((a) => ({
    id: 'aviso-' + a.id, label: a.label, nivel: 'aviso', ordem: 99, segundos: null, direita: 'Atenção', detalhe: a.texto, ultima: '',
  }))
  return [...dosMotores, ...dosAvisos].sort((a, b) => (ORDEM_NIVEL[a.nivel] - ORDEM_NIVEL[b.nivel]) || (a.ordem - b.ordem))
})

const resumo = computed(() => {
  const conta = (n) => linhas.value.filter((l) => l.nivel === n).length
  const erros = conta('erro')
  if (erros) return { nivel: 'erro', texto: erros === 1 ? '1 sincronização com falha' : `${erros} sincronizações com falha` }
  const rodando = conta('rodando')
  if (rodando) return { nivel: 'rodando', texto: `${rodando} em andamento` }
  const avisos = conta('aviso')
  if (avisos) return { nivel: 'aviso', texto: avisos === 1 ? '1 aviso' : `${avisos} avisos` }
  const menor = (nivel) => {
    const segs = linhas.value.filter((l) => l.nivel === nivel && l.segundos != null).map((l) => l.segundos)
    return segs.length ? Math.min(...segs) : null
  }
  if (conta('ocioso')) {
    const prox = menor('ocioso')
    return { nivel: 'ok', texto: prox != null ? `Tudo em dia · próxima em ${relogio(prox)}` : 'Tudo em dia' }
  }
  const inicio = menor('fora')
  return { nivel: 'pausa', texto: inicio != null ? `Fora do horário · inicia em ${relogio(inicio)}` : 'Sincronização em pausa' }
})
</script>

<style scoped>
.ssp {
  position: relative;
  font-size: 0.8rem;
  line-height: 1.2;
  --ssp-ok: #1abc9c;
  --ssp-erro: #e7515a;
  --ssp-rodando: #4361ee;
  --ssp-aviso: #e2a03f;
  --ssp-pausa: #888ea8;
}

.ssp-card {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 300px;
  padding: 6px 10px;
  border: 1px solid #e0e6ed;
  border-radius: 6px;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
  white-space: nowrap;
}
.ssp-card:focus-visible { outline: 2px solid var(--ssp-rodando); outline-offset: 2px; }
.ssp-texto { overflow: hidden; text-overflow: ellipsis; }
.ssp-seta { flex: none; opacity: 0.6; transition: transform 0.15s; }
.ssp-seta-aberta { transform: rotate(180deg); }

/* cor da borda do card fechado acompanha o estado geral */
.ssp-card.ssp-ok { border-color: var(--ssp-ok); }
.ssp-card.ssp-erro { border-color: var(--ssp-erro); }
.ssp-card.ssp-rodando { border-color: var(--ssp-rodando); }
.ssp-card.ssp-aviso { border-color: var(--ssp-aviso); }
.ssp-card.ssp-pausa { border-color: var(--ssp-pausa); }

.ssp-dot, .ssp-spin { flex: none; width: 9px; height: 9px; border-radius: 50%; margin-top: 3px; }
.ssp-ok .ssp-dot, .ssp-ocioso .ssp-dot { background: var(--ssp-ok); }
.ssp-erro .ssp-dot { background: var(--ssp-erro); }
.ssp-aviso .ssp-dot { background: var(--ssp-aviso); }
.ssp-pausa .ssp-dot, .ssp-fora .ssp-dot { background: var(--ssp-pausa); }
.ssp-card .ssp-dot, .ssp-card .ssp-spin { margin-top: 0; }
.ssp-spin {
  border: 2px solid var(--ssp-rodando);
  border-right-color: transparent;
  animation: ssp-gira 0.8s linear infinite;
}
@keyframes ssp-gira { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .ssp-spin { animation-duration: 2.4s; } }

.ssp-pop {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 1100;
  width: 380px;
  max-width: 92vw;
  max-height: 60vh;
  overflow-y: auto;
  padding: 8px 0;
  background: #fff;
  color: #3b3f5c;
  border: 1px solid #e0e6ed;
  border-radius: 6px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.15);
}
.ssp-pop-titulo {
  padding: 2px 14px 6px;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  opacity: 0.6;
}
.ssp-linha { display: flex; gap: 10px; padding: 7px 14px; }
.ssp-linha + .ssp-linha { border-top: 1px solid rgba(136, 142, 168, 0.18); }
.ssp-linha-corpo { flex: 1; min-width: 0; }
.ssp-linha-topo { display: flex; justify-content: space-between; gap: 10px; }
.ssp-linha-nome { font-weight: 600; }
.ssp-linha-dir { flex: none; font-variant-numeric: tabular-nums; opacity: 0.85; }
.ssp-linha-detalhe, .ssp-linha-ultima { margin-top: 2px; opacity: 0.7; overflow-wrap: anywhere; }
.ssp-linha.ssp-erro .ssp-linha-dir, .ssp-linha.ssp-erro .ssp-linha-detalhe { color: var(--ssp-erro); opacity: 1; }
.ssp-linha.ssp-aviso .ssp-linha-dir { color: var(--ssp-aviso); opacity: 1; }
.ssp-linha.ssp-rodando .ssp-linha-dir { color: var(--ssp-rodando); opacity: 1; }

/* tema escuro (mesmas cores do header em dark.scss) */
.dark .ssp-card { border-color: #1b2e4b; color: #bfc9d4; }
.dark .ssp-card.ssp-ok { border-color: var(--ssp-ok); }
.dark .ssp-card.ssp-erro { border-color: var(--ssp-erro); }
.dark .ssp-card.ssp-rodando { border-color: var(--ssp-rodando); }
.dark .ssp-card.ssp-aviso { border-color: var(--ssp-aviso); }
.dark .ssp-pop { background: #0e1726; color: #bfc9d4; border-color: #1b2e4b; box-shadow: 0 6px 18px rgba(0, 0, 0, 0.5); }
.dark .ssp-linha.ssp-rodando .ssp-linha-dir { color: #7b93ff; }

@media (max-width: 575px) {
  .ssp-card { max-width: 200px; }
}
</style>
